import { yupResolver } from '@hookform/resolvers/yup';
import { useShopsysForm } from 'hooks/forms/UseShopsysForm';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ContactFormType } from 'types/form';
import { object, string } from 'yup';

export const useContactForm = (): [UseFormReturn<ContactFormType>, ContactFormType] => {
    const { t } = useTranslation();
    const userData = useCurrentUserData();

    const resolver = yupResolver(
        object().shape({
            email: string().required(t('Please enter email')).email(t('This value is not a valid email')).min(5),
            name: string().required(t('Please enter your name')),
            message: string().required(t('Please enter a message')),
        }),
    );
    const defaultValues = useMemo(
        () => ({
            email: userData.user?.email ?? '',
            name: userData.user?.firstName ?? '',
            message: '',
            subject: '',
        }),
        [userData],
    );

    return [useShopsysForm(resolver, defaultValues), defaultValues];
};

type ContactFormMetaType = {
    formName: string;
    messages: {
        error: string;
        success: string;
    };
    fields: {
        [key in keyof ContactFormType]: {
            name: key;
            label: string;
            errorMessage: string | undefined;
        };
    };
};

export const useContactFormMeta = (formProviderMethods: UseFormReturn<ContactFormType>): ContactFormMetaType => {
    const { t } = useTranslation();
    const errors = formProviderMethods.formState.errors;

    return useMemo(
        () => ({
            formName: 'contact-form',
            messages: {
                error: t('The message could not be sent'),
                success: t('Thank you! Your message was successfully sent.'),
            },
            fields: {
                email: {
                    name: 'email' as const,
                    label: t('Your email'),
                    errorMessage: errors.email?.message,
                },
                name: {
                    name: 'name' as const,
                    label: t('Your name'),
                    errorMessage: errors.name?.message,
                },
                message: {
                    name: 'message' as const,
                    label: t('Message'),
                    errorMessage: errors.message?.message,
                },
                subject: {
                    name: 'subject' as const,
                    label: t('Subject'),
                    errorMessage: undefined,
                },
            },
        }),
        [errors.email?.message, errors.name?.message, errors.message?.message, t],
    );
};
